import { z } from 'zod';
import { WallClockSchema, type ExceptionKind } from './attendance';

/**
 * Phase 3 M3: pay periods, the payroll spreadsheet and clocking health
 * (docs/specs/phase-3-time-attendance.md §2 "Payroll", §4 screens 5–6, D48, D57).
 *
 * A pay period is a calendar month. Its state:
 *   open    managers resolve; the payroll is computed live
 *   review  HR has started checking it; managers still resolve
 *   closed  HR signed it off: a snapshot answers the export, and resolutions, absences and
 *           holiday changes dated inside it are refused (422 period_closed) until HR reopens it
 *   legacy  a month before Aroo ran payroll (no row, older than last month): nothing is locked,
 *           but its resolutions can no longer be undone (the M2 rule)
 */

const IsoDate = z.iso.date();

export const PayPeriodStatusSchema = z.enum(['open', 'review', 'closed', 'legacy']);
export type PayPeriodStatus = z.infer<typeof PayPeriodStatusSchema>;

export const PAY_PERIOD_STATUS_LABELS: Record<PayPeriodStatus, string> = {
  open: 'Open',
  review: 'In review',
  closed: 'Closed',
  legacy: 'Before Aroo payroll',
};

/** How the open no-shows / missing clock-outs a period was closed with are paid. */
export const OpenTreatmentSchema = z.enum(['unpaid', 'paid']);
export type OpenTreatment = z.infer<typeof OpenTreatmentSchema>;

export const PayPeriodSchema = z.object({
  /** "YYYY-MM". */
  key: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  status: PayPeriodStatusSchema,
  /** No row yet: the state follows from the date (open for this month and last, else legacy). */
  implicit: z.boolean(),
  /** The month is over (it can be reviewed and closed). */
  ended: z.boolean(),
  reviewStartedBy: z.string().nullable(),
  reviewStartedAt: z.iso.datetime().nullable(),
  closedBy: z.string().nullable(),
  closedAt: z.iso.datetime().nullable(),
  /** Open blocking exceptions HR closed it with, and how they were paid. */
  closedWithOpen: z.number().int().nullable(),
  openTreatment: OpenTreatmentSchema.nullable(),
  reopenedBy: z.string().nullable(),
  reopenedAt: z.iso.datetime().nullable(),
  reopenReason: z.string().nullable(),
  snapshotId: z.uuid().nullable(),
});
export type PayPeriod = z.infer<typeof PayPeriodSchema>;

export const PayPeriodsResponseSchema = z.object({
  today: IsoDate,
  periods: z.array(PayPeriodSchema),
});
export type PayPeriodsResponse = z.infer<typeof PayPeriodsResponseSchema>;

// Columns (the HR-editable category → column table) ------------------------------------------

/**
 * `lateness` also takes late/early minutes nobody gave a business reason for; `other` takes any
 * HR (unpaid) reason no column lists; `open_items` takes the open no-shows and missing clock-outs.
 */
export const PayrollColumnSpecialSchema = z.enum(['none', 'lateness', 'other', 'open_items']);
export type PayrollColumnSpecial = z.infer<typeof PayrollColumnSpecialSchema>;

export const PayrollColumnSchema = z.object({
  code: z.string(),
  label: z.string(),
  sort: z.number().int(),
  special: PayrollColumnSpecialSchema,
  /** Resolution reasons whose (unpaid) hours land here; a reason belongs to one column. */
  reasonCodes: z.array(z.string()),
  /** Absence types whose days land here (also reasons linked to them that no column lists). */
  absenceTypes: z.array(z.string()),
  active: z.boolean(),
});
export type PayrollColumn = z.infer<typeof PayrollColumnSchema>;

export const PayrollColumnsResponseSchema = z.object({ columns: z.array(PayrollColumnSchema) });
export type PayrollColumnsResponse = z.infer<typeof PayrollColumnsResponseSchema>;

const ColumnLabel = z.string().trim().min(2).max(80);
const Codes = z.array(z.string().trim().min(1).max(40)).max(60);

/** POST /attendance/payroll/columns */
export const PayrollColumnCreateSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lower-case letters, digits and _, 2–40 long'),
  label: ColumnLabel,
  sort: z.number().int().min(0).max(9999).optional(),
  reasonCodes: Codes.optional(),
  absenceTypes: Codes.optional(),
  active: z.boolean().optional(),
});
export type PayrollColumnCreate = z.infer<typeof PayrollColumnCreateSchema>;

/** PATCH /attendance/payroll/columns/:code (never deleted: `active: false` hides it). */
export const PayrollColumnPatchSchema = z
  .object({
    label: ColumnLabel.optional(),
    sort: z.number().int().min(0).max(9999).optional(),
    reasonCodes: Codes.optional(),
    absenceTypes: Codes.optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Change at least one field' });
export type PayrollColumnPatch = z.infer<typeof PayrollColumnPatchSchema>;

export const PayrollColumnWriteResponseSchema = z.object({ column: PayrollColumnSchema });
export type PayrollColumnWriteResponse = z.infer<typeof PayrollColumnWriteResponseSchema>;

// The payroll -----------------------------------------------------------------------------------

export const PayrollPersonSchema = z.object({
  personId: z.uuid(),
  staffNumber: z.string().nullable(),
  givenName: z.string(),
  familyName: z.string(),
  name: z.string(),
  siteCode: z.string().nullable(),
  siteName: z.string().nullable(),
  /** Contracted hours a week from the staff record (often unknown). */
  contractHoursWeekly: z.number().nullable(),
  doesNotClock: z.boolean(),
  casual: z.boolean(),
  /** The pattern's hours for every working day employed in the period (incl. days off). */
  patternHours: z.number(),
  /** Hours due (working days not on holiday / absence / bank holiday). */
  expectedHours: z.number(),
  /** Clocked hours less breaks, using corrected times. */
  workedHours: z.number(),
  /** Hours not worked but paid: late / early / no-show / missing clock-out with a business reason. */
  paidBusinessHours: z.number(),
  /** Unpaid hours per column code (payroll_columns). */
  unpaid: z.record(z.string(), z.number()),
  /** The deductions to key: every column, the open-items one only when treated as unpaid. */
  unpaidTotal: z.number(),
  /** D53: approved casual hours. */
  casualHours: z.number(),
  /** Casual shifts not approved yet (not paid). */
  casualPendingHours: z.number(),
  holidayDays: z.number(),
  holidayHours: z.number(),
  bankHolidayDays: z.number(),
  bankHolidayHours: z.number(),
  /** Absence on a type no column lists that is not unpaid (bereavement, jury, training…). */
  paidAbsenceHours: z.number(),
  /** Clocked on a day not due (information only: overtime is not paid, D52). */
  unplannedHours: z.number(),
  /** Open exceptions that affect pay (no-show, late, early, missing clock-out, casual). */
  openExceptions: z.number().int(),
  /** Days whose worked time is unknown, and timed-pattern gaps, for HR to check. */
  unknownWorkedDays: z.number().int(),
  untimedDays: z.number().int(),
  flags: z.array(z.string()),
});
export type PayrollPerson = z.infer<typeof PayrollPersonSchema>;

export const PayrollTotalsSchema = z.object({
  people: z.number().int(),
  expectedHours: z.number(),
  workedHours: z.number(),
  paidBusinessHours: z.number(),
  unpaid: z.record(z.string(), z.number()),
  unpaidTotal: z.number(),
  casualHours: z.number(),
  holidayDays: z.number(),
  holidayHours: z.number(),
  bankHolidayDays: z.number(),
  openExceptions: z.number().int(),
});
export type PayrollTotals = z.infer<typeof PayrollTotalsSchema>;

export const PayrollBlockerSiteSchema = z.object({
  siteId: z.uuid().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  count: z.number().int(),
  byKind: z.record(z.string(), z.number().int()),
});
export type PayrollBlockerSite = z.infer<typeof PayrollBlockerSiteSchema>;

export const PayrollResponseSchema = z.object({
  period: PayPeriodSchema,
  /** `snapshot` once closed: the figures as signed off, whatever changed since. */
  source: z.enum(['live', 'snapshot']),
  from: IsoDate,
  to: IsoDate,
  generatedAt: z.iso.datetime(),
  generatedBy: z.string().nullable(),
  /** How open items count in the totals shown (the period's choice once closed). */
  openTreatment: OpenTreatmentSchema,
  columns: z.array(PayrollColumnSchema),
  people: z.array(PayrollPersonSchema),
  totals: PayrollTotalsSchema,
  /** Open exceptions that block closing, by home site. */
  blockers: z.object({ total: z.number().int(), sites: z.array(PayrollBlockerSiteSchema) }),
});
export type PayrollResponse = z.infer<typeof PayrollResponseSchema>;

// Lifecycle ---------------------------------------------------------------------------------------

export const PayPeriodReviewSchema = z.object({}).strict();

/** POST …/close. With open blockers, `acknowledgeOpen` must equal their count (HR ticked it). */
export const PayPeriodCloseSchema = z.object({
  acknowledgeOpen: z.number().int().min(0).optional(),
  openTreatment: OpenTreatmentSchema.optional(),
});
export type PayPeriodClose = z.infer<typeof PayPeriodCloseSchema>;

export const PayPeriodReopenSchema = z.object({ reason: z.string().trim().min(5).max(500) });
export type PayPeriodReopen = z.infer<typeof PayPeriodReopenSchema>;

export const PayPeriodWriteResponseSchema = z.object({ period: PayPeriodSchema });
export type PayPeriodWriteResponse = z.infer<typeof PayPeriodWriteResponseSchema>;

/** The kinds whose open exceptions block closing (they change what is paid). */
export const PAYROLL_BLOCKING_KINDS: readonly ExceptionKind[] = [
  'no_show',
  'late_in',
  'early_out',
  'missing_out',
  'casual_shift',
];

// Clocking health ---------------------------------------------------------------------------------

export const ClockingHealthSiteSchema = z.object({
  siteId: z.uuid().nullable(),
  /** The clocks' own branch code. */
  code: z.string(),
  name: z.string(),
  lastSeen: WallClockSchema.nullable(),
  readingsToday: z.number().int(),
  readings7d: z.number().int(),
  readings30d: z.number().int(),
  /** Share (0–1) of finished readings over 30 days with no real clock-out. */
  missingOutRate: z.number().nullable(),
  autoClosedRate: z.number().nullable(),
  /** Share keyed by hand in the old T&A screens. */
  manualRate: z.number().nullable(),
  unknownStaff: z.number().int(),
});
export type ClockingHealthSite = z.infer<typeof ClockingHealthSiteSchema>;

export const ClockingDeviceSchema = z.object({
  deviceId: z.string(),
  branch: z.string().nullable(),
  status: z.string().nullable(),
  /** Whatever "last seen / updated" column dt_spar has, as its wall clock. */
  lastSeen: z.string().nullable(),
});
export type ClockingDevice = z.infer<typeof ClockingDeviceSchema>;

export const ClockingHealthResponseSchema = z.object({
  today: IsoDate,
  /** When the clocks were last read from the old system. */
  lastSyncAt: z.iso.datetime().nullable(),
  sites: z.array(ClockingHealthSiteSchema),
  unknownStaffTotal: z.number().int(),
  devices: z.object({
    available: z.boolean(),
    note: z.string().nullable(),
    items: z.array(ClockingDeviceSchema),
  }),
});
export type ClockingHealthResponse = z.infer<typeof ClockingHealthResponseSchema>;

// Rules shared by the API and the web --------------------------------------------------------------

/** "2026-09" → "September 2026". */
export function payPeriodLabel(key: string): string {
  const [y, m] = key.split('-').map(Number) as [number, number];
  const name = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${name} ${y}`;
}

/** The month before "YYYY-MM". */
export function previousMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}
